import { logger } from 'module/logger';
import { SignalOccurrencesAPI } from 'api/signal-occurrences';
import { Timeline, GroupedTimelines } from 'api/timeline/types';
import moment from 'moment';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  GroupByType,
  getPeriodKeyByOffset,
  getDateRangeForPeriod,
  getPeriodStart,
  addPeriods,
  findPeriodIndex,
} from './utils';

const ITEMS_PER_BATCH = 3;
const ITEMS_ABOVE = 3;
const PERIODS_BEFORE_AFTER = 2;

interface TimelinePeriod {
  periodKey: string;
  timelines: Timeline[];
}

interface UseTimelineDataProps {
  companyId: number;
  groupBy: GroupByType;
  billingStartDate?: string;
  view?: string;
}

// Helper to get custom week period key (1-7, 8-14, etc.)
const getCustomWeekPeriodKey = (date: moment.Moment): string => {
  const year = date.year();
  const month = date.month() + 1; // moment months are 0-indexed
  const day = date.date();
  const weekOfMonth = Math.ceil(day / 7);

  return `${year}-${month.toString().padStart(2, '0')}-W${weekOfMonth}`;
};

// Helper to get custom week period start
const getCustomWeekPeriodStart = (periodKey: string): moment.Moment => {
  const match = periodKey.match(/^(\d{4})-(\d{2})-W(\d)$/);

  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // moment months are 0-indexed
    const weekOfMonth = parseInt(match[3], 10);
    const startDay = (weekOfMonth - 1) * 7 + 1;

    return moment([year, month, startDay]).startOf('day');
  }

  return moment(periodKey).startOf('day');
};

// Helper to get custom week period end
const getCustomWeekPeriodEnd = (periodKey: string): moment.Moment => {
  const match = periodKey.match(/^(\d{4})-(\d{2})-W(\d)$/);

  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const weekOfMonth = parseInt(match[3], 10);
    const endDay = Math.min(
      weekOfMonth * 7,
      moment([year, month]).daysInMonth(),
    );

    return moment([year, month, endDay]).endOf('day');
  }

  return moment(periodKey).endOf('day');
};

// Helper to get date range for custom week period
const getCustomWeekDateRange = (
  periodKey: string,
): { startDate: string; endDate: string } => {
  const startDate = getCustomWeekPeriodStart(periodKey);
  const endDate = getCustomWeekPeriodEnd(periodKey);
  const today = moment().endOf('day');

  const finalEndDate = endDate.isAfter(today) ? today : endDate;

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: finalEndDate.format('YYYY-MM-DD'),
  };
};

// Helper to add custom week periods
const addCustomWeekPeriods = (periodKey: string, amount: number): string => {
  const match = periodKey.match(/^(\d{4})-(\d{2})-W(\d)$/);

  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const weekOfMonth = parseInt(match[3], 10);
    const startDay = (weekOfMonth - 1) * 7 + 1;
    const date = moment([year, month, startDay]).add(amount * 7, 'days');

    return getCustomWeekPeriodKey(date);
  }

  return periodKey;
};

// Override period key generation for WEEK to use custom weeks
const getPeriodKeyByOffsetCustom = (
  offset: number,
  groupBy: GroupByType,
): string => {
  if (groupBy === 'WEEK') {
    // For offset 0, return current week
    if (offset === 0) {
      return getCustomWeekPeriodKey(moment());
    }
    // For other offsets, calculate from current period
    const currentPeriod = getCustomWeekPeriodKey(moment());

    return addCustomWeekPeriods(currentPeriod, offset);
  }

  return getPeriodKeyByOffset(offset, groupBy);
};

// Override date range for WEEK to use custom weeks
const getDateRangeForPeriodCustom = (
  periodKey: string,
  groupBy: GroupByType,
): { startDate: string; endDate: string } => {
  if (groupBy === 'WEEK') {
    return getCustomWeekDateRange(periodKey);
  }

  return getDateRangeForPeriod(periodKey, groupBy);
};

// Override period start for WEEK to use custom weeks
const getPeriodStartCustom = (
  periodKey: string,
  groupBy: GroupByType,
): moment.Moment => {
  if (groupBy === 'WEEK') {
    return getCustomWeekPeriodStart(periodKey);
  }

  return getPeriodStart(periodKey, groupBy);
};

// Override add periods for WEEK to use custom weeks
const addPeriodsCustom = (
  periodKey: string,
  amount: number,
  groupBy: GroupByType,
): string => {
  if (groupBy === 'WEEK') {
    return addCustomWeekPeriods(periodKey, amount);
  }

  return addPeriods(periodKey, amount, groupBy);
};

/** Get period key for a date string and groupBy. */
export const getPeriodKeyForDate = (
  date: string,
  groupBy: GroupByType,
): string => {
  const m = moment(date);

  if (!m.isValid()) {
    return getPeriodKeyByOffsetCustom(0, groupBy);
  }

  if (groupBy === 'MONTH') {
    return m.format('YYYY-MM');
  }

  return getCustomWeekPeriodKey(m);
};

// Helper to ensure a period exists in the periods array (adds empty period if missing)
const ensurePeriodExists = (
  periods: TimelinePeriod[],
  periodKey: string,
): TimelinePeriod[] => {
  const periodExists = periods.some((p) => p.periodKey === periodKey);

  if (!periodExists) {
    return [...periods, { periodKey, timelines: [] }];
  }

  return periods;
};

// Helper to group timelines by period from GroupedTimelines response
const groupTimelinesByPeriod = (
  groupedTimelines: GroupedTimelines,
  groupBy: GroupByType,
  requestedPeriodKey?: string,
): TimelinePeriod[] => {
  const periodMap = new Map<string, Timeline[]>();

  // Iterate through all date keys in the grouped response
  Object.entries(groupedTimelines).forEach(([dateKey, timelines]) => {
    const date = moment(dateKey, 'YYYY-MM-DD');

    if (!date.isValid()) {
      logger.warn('Invalid date key in grouped timelines', { dateKey });

      return;
    }

    // Determine which period this date belongs to
    let periodKey: string;

    if (groupBy === 'WEEK') {
      periodKey = getCustomWeekPeriodKey(date);
    } else if (groupBy === 'QUARTER') {
      const quarter = Math.floor(date.month() / 3) + 1;

      periodKey = `${date.year()}-Q${quarter}`;
    } else {
      // MONTH
      periodKey = date.format('YYYY-MM');
    }

    // Add timelines to the period
    if (!periodMap.has(periodKey)) {
      periodMap.set(periodKey, []);
    }

    periodMap.get(periodKey)!.push(...timelines);
  });

  // Convert map to array and sort by period (newest first)
  let periods: TimelinePeriod[] = Array.from(periodMap.entries())
    .map(([periodKey, timelines]) => ({
      periodKey,
      timelines,
    }))
    .sort((a, b) => {
      const dateA = getPeriodStartCustom(a.periodKey, groupBy);
      const dateB = getPeriodStartCustom(b.periodKey, groupBy);

      return dateB.valueOf() - dateA.valueOf();
    });

  // If a specific period was requested and it's not in the result, add it as empty
  if (requestedPeriodKey) {
    periods = ensurePeriodExists(periods, requestedPeriodKey);
    // Re-sort after adding the period
    periods.sort((a, b) => {
      const dateA = getPeriodStartCustom(a.periodKey, groupBy);
      const dateB = getPeriodStartCustom(b.periodKey, groupBy);

      return dateB.valueOf() - dateA.valueOf();
    });
  }

  return periods;
};

// Helper to merge timeline periods, deduplicating by periodKey
const mergeTimelinePeriods = (
  existing: TimelinePeriod[],
  newPeriods: TimelinePeriod[],
  groupBy: GroupByType,
): TimelinePeriod[] => {
  const periodMap = new Map<string, Timeline[]>();

  // Add existing periods
  existing.forEach((period) => {
    periodMap.set(period.periodKey, [...period.timelines]);
  });

  // Merge new periods (combine timelines if period key exists)
  newPeriods.forEach((period) => {
    if (periodMap.has(period.periodKey)) {
      // Merge timelines, avoiding duplicates by source.id
      const existingTimelines = periodMap.get(period.periodKey)!;
      const existingIds = new Set(existingTimelines.map((t) => t.source.id));
      const newTimelines = period.timelines.filter(
        (t) => !existingIds.has(t.source.id),
      );

      periodMap.set(period.periodKey, [...existingTimelines, ...newTimelines]);
    } else {
      periodMap.set(period.periodKey, [...period.timelines]);
    }
  });

  // Convert back to array and sort by date (newest first)
  return Array.from(periodMap.entries())
    .map(([periodKey, timelines]) => ({
      periodKey,
      timelines,
    }))
    .sort((a, b) => {
      const dateA = getPeriodStartCustom(a.periodKey, groupBy);
      const dateB = getPeriodStartCustom(b.periodKey, groupBy);

      return dateB.valueOf() - dateA.valueOf();
    });
};

export const useTimelineData = ({
  companyId,
  groupBy,
  billingStartDate = '2020-04-01',
  view,
}: UseTimelineDataProps) => {
  const [allData, setAllData] = useState<TimelinePeriod[]>([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isLoadingSelectedPeriod, setIsLoadingSelectedPeriod] =
    useState<boolean>(false);
  const [isLoadingDown, setIsLoadingDown] = useState<boolean>(false);
  const [isLoadingUp, setIsLoadingUp] = useState<boolean>(false);
  const [isLoadingTOC, setIsLoadingTOC] = useState<boolean>(false);
  const [loadedPeriods, setLoadedPeriods] = useState<Set<string>>(new Set());
  const fetchingPeriodsRef = useRef<Set<string>>(new Set());
  const lastGroupByViewRef = useRef<string>('');

  // Get current period key based on groupBy
  const currentSystemPeriod = useMemo(() => {
    return getPeriodKeyByOffsetCustom(0, groupBy);
  }, [groupBy]);

  // Create a key to track groupBy/view combination
  const groupByViewKey = `${groupBy}-${view || 'none'}`;

  // Reset data when groupBy or view changes
  useEffect(() => {
    // Only reset if groupBy/view actually changed
    if (lastGroupByViewRef.current !== groupByViewKey) {
      setAllData([]);
      setLoadedPeriods(new Set());
      setSelectedPeriodKey('');
      fetchingPeriodsRef.current.clear();
      setIsInitializing(true);
      setHasInitialized(false);
      lastGroupByViewRef.current = groupByViewKey;
    }
  }, [groupBy, view, groupByViewKey]);

  // Initialize with current period and preload surrounding periods
  useEffect(() => {
    const initializeData = async () => {
      // Check if we're already initialized for the current groupBy/view
      const isCurrentGroupByView =
        lastGroupByViewRef.current === groupByViewKey;

      // Skip if already initialized for this groupBy/view combination
      // Only skip if BOTH conditions are true: initialized AND matching groupBy/view
      // If groupBy/view changed, we need to re-initialize even if hasInitialized is true
      if (hasInitialized && isCurrentGroupByView && allData.length > 0) {
        setIsInitializing(false);

        return;
      }

      // If groupBy/view changed, ensure reset has completed
      // Wait for state to settle if we're switching groupBy/view
      if (!isCurrentGroupByView) {
        // Wait a tick to ensure reset effect has completed
        await new Promise((resolve) => setTimeout(resolve, 0));

        // Double-check we're still targeting the correct groupBy/view
        if (lastGroupByViewRef.current !== groupByViewKey) {
          // GroupBy/view changed again during wait, abort
          return;
        }
      }

      // Ensure we're initializing for the correct groupBy/view
      setIsInitializing(true);
      const currentPeriod = currentSystemPeriod;

      setSelectedPeriodKey(currentPeriod);

      try {
        const dateRange = getDateRangeForPeriodCustom(currentPeriod, groupBy);

        logger.info('Initializing timeline data for current period', {
          period: currentPeriod,
          dateRange,
          groupBy,
          companyId,
        });

        const requestParams: any = {
          groupBy: groupBy,
          startTime: dateRange.startDate,
          endTime: dateRange.endDate,
        };

        if (view) {
          requestParams.view = view;
        }
        const response = await SignalOccurrencesAPI.getTimeline(
          companyId,
          requestParams,
        );

        // Group timelines by period from the grouped response
        // Always pass currentPeriod to ensure it's included even if empty
        const periods = groupTimelinesByPeriod(
          response || {},
          groupBy,
          currentPeriod,
        );

        // Ensure current period exists in the periods array
        const currentPeriodExists = periods.some(
          (p) => p.periodKey === currentPeriod,
        );

        if (!currentPeriodExists) {
          periods.push({ periodKey: currentPeriod, timelines: [] });
        }

        // Sort periods by date (newest first)
        periods.sort((a, b) => {
          const dateA = getPeriodStartCustom(a.periodKey, groupBy);
          const dateB = getPeriodStartCustom(b.periodKey, groupBy);

          return dateB.valueOf() - dateA.valueOf();
        });

        const initialLoadedPeriods = new Set(periods.map((p) => p.periodKey));

        setAllData(periods);

        setLoadedPeriods(initialLoadedPeriods);

        // Preload surrounding periods (both before and after current period)
        const periodsToPreload: string[] = [];
        const lowerCapDate = moment(billingStartDate);
        let lowerCapPeriod: string;

        if (groupBy === 'WEEK') {
          lowerCapPeriod = getCustomWeekPeriodKey(lowerCapDate);
        } else if (groupBy === 'QUARTER') {
          const quarter = Math.floor(lowerCapDate.month() / 3) + 1;

          lowerCapPeriod = `${lowerCapDate.year()}-Q${quarter}`;
        } else {
          lowerCapPeriod = lowerCapDate.format('YYYY-MM');
        }

        const lowerCapPeriodDate = getPeriodStartCustom(
          lowerCapPeriod,
          groupBy,
        );

        // Preload periods before current (for scrolling down)
        for (let i = 1; i <= PERIODS_BEFORE_AFTER; i++) {
          const periodKey = addPeriodsCustom(currentPeriod, -i, groupBy);
          const periodDate = getPeriodStartCustom(periodKey, groupBy);

          if (periodDate.isSameOrAfter(lowerCapPeriodDate)) {
            if (!initialLoadedPeriods.has(periodKey)) {
              periodsToPreload.push(periodKey);
            }
          }
        }

        // Preload periods after current (for scrolling up)
        for (let i = 1; i <= ITEMS_ABOVE; i++) {
          const periodKey = getPeriodKeyByOffsetCustom(i, groupBy);
          const periodDate = getPeriodStartCustom(periodKey, groupBy);
          const currentPeriodDate = getPeriodStartCustom(
            currentPeriod,
            groupBy,
          );

          if (periodDate.isAfter(currentPeriodDate)) {
            break;
          }

          if (periodDate.isSameOrAfter(lowerCapPeriodDate)) {
            if (!initialLoadedPeriods.has(periodKey)) {
              periodsToPreload.push(periodKey);
            }
          }
        }

        const preloadPromises = periodsToPreload.map(async (periodKey) => {
          try {
            const periodDateRange = getDateRangeForPeriodCustom(
              periodKey,
              groupBy,
            );

            const preloadRequestParams: any = {
              groupBy: groupBy,
              startTime: periodDateRange.startDate,
              endTime: periodDateRange.endDate,
            };

            if (view) {
              preloadRequestParams.view = view;
            }

            const preloadResponse = await SignalOccurrencesAPI.getTimeline(
              companyId,
              preloadRequestParams,
            );

            // Group timelines by period from the grouped response
            const periods = groupTimelinesByPeriod(
              preloadResponse || {},
              groupBy,
              periodKey,
            );

            return periods;
          } catch (error) {
            logger.error('Error preloading timeline period', {
              periodKey,
              error,
            });

            return [];
          }
        });

        const preloadResults = await Promise.all(preloadPromises);
        const allPreloadData = preloadResults.flat();

        if (allPreloadData.length > 0) {
          const merged = mergeTimelinePeriods(periods, allPreloadData, groupBy);

          setAllData(merged);
          setLoadedPeriods(new Set(merged.map((p) => p.periodKey)));
        } else {
          setAllData(periods);
          setLoadedPeriods(new Set(periods.map((p) => p.periodKey)));
        }
      } catch (error) {
        logger.error('Error initializing timeline data', {
          error,
          groupBy,
          companyId,
        });
        setAllData([]);
        setLoadedPeriods(new Set());
      } finally {
        setIsInitializing(false);
        setHasInitialized(true);
        lastGroupByViewRef.current = groupByViewKey;
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, groupBy, view, groupByViewKey, hasInitialized]);

  // Handle period selection from TOC
  const handlePeriodSelect = useCallback(
    async (periodKey: string) => {
      if (periodKey === selectedPeriodKey) {
        return;
      }

      setSelectedPeriodKey(periodKey);

      const periodsToLoad: string[] = [];

      for (let i = -PERIODS_BEFORE_AFTER; i <= PERIODS_BEFORE_AFTER; i++) {
        const checkPeriodKey = addPeriodsCustom(periodKey, i, groupBy);

        if (
          findPeriodIndex(
            allData.map((d) => ({ periodKey: d.periodKey }) as any),
            checkPeriodKey,
            groupBy,
          ) < 0
        ) {
          periodsToLoad.push(checkPeriodKey);
        }
      }

      if (periodsToLoad.length === 0) {
        return;
      }

      setIsLoadingSelectedPeriod(true);

      try {
        const lowerCapDate = moment(billingStartDate);
        const currentPeriod = getPeriodKeyByOffsetCustom(0, groupBy);

        let lowerCapPeriod: string;

        if (groupBy === 'WEEK') {
          lowerCapPeriod = getCustomWeekPeriodKey(lowerCapDate);
        } else if (groupBy === 'QUARTER') {
          const quarter = Math.floor(lowerCapDate.month() / 3) + 1;

          lowerCapPeriod = `${lowerCapDate.year()}-Q${quarter}`;
        } else {
          lowerCapPeriod = lowerCapDate.format('YYYY-MM');
        }

        const validPeriodsToLoad = periodsToLoad.filter((p) => {
          const periodDate = getPeriodStartCustom(p, groupBy);
          const lowerCapPeriodDate = getPeriodStartCustom(
            lowerCapPeriod,
            groupBy,
          );
          const currentPeriodDate = getPeriodStartCustom(
            currentPeriod,
            groupBy,
          );

          return (
            periodDate.isSameOrAfter(lowerCapPeriodDate) &&
            periodDate.isSameOrBefore(currentPeriodDate)
          );
        });

        const fetchPromises = validPeriodsToLoad.map(async (periodKey) => {
          try {
            const dateRange = getDateRangeForPeriodCustom(periodKey, groupBy);

            const requestParams: any = {
              groupBy: groupBy,
              startTime: dateRange.startDate,
              endTime: dateRange.endDate,
            };

            if (view) {
              requestParams.view = view;
            }

            const response = await SignalOccurrencesAPI.getTimeline(
              companyId,
              requestParams,
            );

            // Group timelines by period from the grouped response
            return groupTimelinesByPeriod(response || {}, groupBy, periodKey);
          } catch (error) {
            logger.error('Error loading timeline period', { periodKey, error });

            return [];
          }
        });

        const results = await Promise.all(fetchPromises);
        const allNewData = results.flat();

        // Always ensure the selected period exists, even if API returns no data
        // This is important for future periods or periods with no data
        let merged: TimelinePeriod[];

        if (allNewData.length > 0) {
          merged = mergeTimelinePeriods(allData, allNewData, groupBy);
        } else {
          // If no data returned, still ensure selected period exists
          merged = ensurePeriodExists([...allData], periodKey);
        }

        // Ensure selected period is always in the merged data
        merged = ensurePeriodExists(merged, periodKey);

        setAllData(merged);
        setLoadedPeriods(new Set(merged.map((p) => p.periodKey)));
      } catch (error) {
        logger.error('Error loading selected timeline period', { error });
      } finally {
        setIsLoadingSelectedPeriod(false);
      }
    },
    [allData, selectedPeriodKey, companyId, billingStartDate, groupBy, view],
  );

  // Load earlier periods (scroll down)
  // silent: if true, don't set isLoadingDown (for TOC loading)
  const loadEarlierPeriods = useCallback(
    async (silent: boolean = false) => {
      if (isLoadingDown || allData.length === 0) {
        return;
      }

      const oldestPeriod = allData[allData.length - 1];
      const oldestPeriodKey = oldestPeriod.periodKey;
      const oldestPeriodDate = getPeriodStartCustom(oldestPeriodKey, groupBy);
      const lowerCapDate = moment(billingStartDate);
      const currentPeriod = getPeriodKeyByOffsetCustom(0, groupBy);
      const currentPeriodDate = getPeriodStartCustom(currentPeriod, groupBy);

      let lowerCapPeriod: string;

      if (groupBy === 'WEEK') {
        lowerCapPeriod = getCustomWeekPeriodKey(lowerCapDate);
      } else if (groupBy === 'QUARTER') {
        const quarter = Math.floor(lowerCapDate.month() / 3) + 1;

        lowerCapPeriod = `${lowerCapDate.year()}-Q${quarter}`;
      } else {
        lowerCapPeriod = lowerCapDate.format('YYYY-MM');
      }

      const lowerCapPeriodDate = getPeriodStartCustom(lowerCapPeriod, groupBy);

      if (oldestPeriodDate.isSameOrBefore(lowerCapPeriodDate)) {
        return;
      }

      const periodsToFetch: string[] = [];

      // Also check if current period is missing and needs to be loaded
      if (
        !loadedPeriods.has(currentPeriod) &&
        !fetchingPeriodsRef.current.has(currentPeriod) &&
        currentPeriodDate.isSameOrAfter(lowerCapPeriodDate) &&
        currentPeriodDate.isBefore(oldestPeriodDate)
      ) {
        periodsToFetch.push(currentPeriod);
        fetchingPeriodsRef.current.add(currentPeriod);
      }

      for (let i = 1; i <= ITEMS_PER_BATCH; i++) {
        const candidatePeriodKey = addPeriodsCustom(
          oldestPeriodKey,
          -i,
          groupBy,
        );
        const candidatePeriodDate = getPeriodStartCustom(
          candidatePeriodKey,
          groupBy,
        );

        if (candidatePeriodDate.isBefore(lowerCapPeriodDate)) {
          break;
        }

        if (candidatePeriodDate.isSame(lowerCapPeriodDate)) {
          if (
            !loadedPeriods.has(candidatePeriodKey) &&
            !fetchingPeriodsRef.current.has(candidatePeriodKey)
          ) {
            periodsToFetch.push(candidatePeriodKey);
            fetchingPeriodsRef.current.add(candidatePeriodKey);
          }
          break;
        }

        if (
          loadedPeriods.has(candidatePeriodKey) ||
          fetchingPeriodsRef.current.has(candidatePeriodKey)
        ) {
          continue;
        }

        periodsToFetch.push(candidatePeriodKey);
        fetchingPeriodsRef.current.add(candidatePeriodKey);
      }

      if (periodsToFetch.length === 0) {
        return;
      }

      // Set appropriate loading state
      if (silent) {
        // Silent loading from TOC - use separate TOC loading state
        setIsLoadingTOC(true);
      } else {
        // Normal loading from main content scroll
        setIsLoadingDown(true);
      }

      try {
        const fetchPromises = periodsToFetch.map(async (periodKey) => {
          try {
            const dateRange = getDateRangeForPeriodCustom(periodKey, groupBy);

            const requestParams: any = {
              groupBy,
              startTime: dateRange.startDate,
              endTime: dateRange.endDate,
            };

            if (view) {
              requestParams.view = view;
            }

            const response = await SignalOccurrencesAPI.getTimeline(
              companyId,
              requestParams,
            );

            // Group timelines by period from the grouped response
            // Pass periodKey to ensure it's included even if empty
            return groupTimelinesByPeriod(response || {}, groupBy, periodKey);
          } catch (error) {
            logger.error('Error loading earlier timeline period', {
              periodKey,
              error,
            });

            // Return empty period if error occurs, so it's still cached
            return [{ periodKey, timelines: [] }];
          }
        });

        const results = await Promise.all(fetchPromises);
        const allNewData = results.flat();

        // Ensure all requested periods are included, even if empty
        // This is important so empty periods show up in TOC with "-"
        let merged = allData;

        if (allNewData.length > 0) {
          merged = mergeTimelinePeriods(allData, allNewData, groupBy);
        }

        // Add any periods that were requested but not returned (empty periods)
        periodsToFetch.forEach((periodKey) => {
          const periodExists = merged.some((p) => p.periodKey === periodKey);

          if (!periodExists) {
            merged = ensurePeriodExists(merged, periodKey);
          }
        });

        // Update state with merged data (including empty periods)
        setAllData(merged);
        setLoadedPeriods(new Set([...loadedPeriods, ...periodsToFetch]));
      } catch (error) {
        logger.error('Error loading earlier timeline periods', { error });
      } finally {
        // Reset appropriate loading state
        if (silent) {
          setIsLoadingTOC(false);
        } else {
          setIsLoadingDown(false);
        }
        periodsToFetch.forEach((key) => fetchingPeriodsRef.current.delete(key));
      }
    },
    [
      allData,
      isLoadingDown,
      loadedPeriods,
      companyId,
      billingStartDate,
      groupBy,
      view,
    ],
  );

  // Load later periods (scroll up)
  const loadLaterPeriods = useCallback(async () => {
    if (isLoadingUp || allData.length === 0) {
      return;
    }

    const newestPeriod = allData[0];
    const newestPeriodKey = newestPeriod.periodKey;
    const newestPeriodDate = getPeriodStartCustom(newestPeriodKey, groupBy);
    const currentPeriod = getPeriodKeyByOffsetCustom(0, groupBy);
    const currentPeriodDate = getPeriodStartCustom(currentPeriod, groupBy);

    if (newestPeriodDate.isSameOrAfter(currentPeriodDate)) {
      return;
    }

    const periodsToFetch: string[] = [];

    for (let i = 1; i <= ITEMS_PER_BATCH; i++) {
      const candidatePeriodKey = addPeriodsCustom(newestPeriodKey, i, groupBy);
      const candidatePeriodDate = getPeriodStartCustom(
        candidatePeriodKey,
        groupBy,
      );

      if (candidatePeriodDate.isAfter(currentPeriodDate)) {
        break;
      }

      if (
        loadedPeriods.has(candidatePeriodKey) ||
        fetchingPeriodsRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      periodsToFetch.push(candidatePeriodKey);
      fetchingPeriodsRef.current.add(candidatePeriodKey);
    }

    if (periodsToFetch.length === 0) {
      return;
    }

    setIsLoadingUp(true);

    try {
      const fetchPromises = periodsToFetch.map(async (periodKey) => {
        try {
          const dateRange = getDateRangeForPeriodCustom(periodKey, groupBy);

          const requestParams: any = {
            groupBy: groupBy,
            startTime: dateRange.startDate,
            endTime: dateRange.endDate,
          };

          if (view) {
            requestParams.view = view;
          }

          const response = await SignalOccurrencesAPI.getTimeline(
            companyId,
            requestParams,
          );

          // Group timelines by period from the grouped response
          return groupTimelinesByPeriod(response || {}, groupBy, periodKey);
        } catch (error) {
          logger.error('Error loading later timeline period', {
            periodKey,
            error,
          });

          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const allNewData = results.flat();

      if (allNewData.length > 0) {
        const merged = mergeTimelinePeriods(allData, allNewData, groupBy);

        setAllData(merged);
        setLoadedPeriods(
          new Set([...loadedPeriods, ...allNewData.map((p) => p.periodKey)]),
        );
      }
    } catch (error) {
      logger.error('Error loading later timeline periods', { error });
    } finally {
      setIsLoadingUp(false);
      periodsToFetch.forEach((key) => fetchingPeriodsRef.current.delete(key));
    }
  }, [allData, isLoadingUp, loadedPeriods, companyId, groupBy, view]);

  // Load period data without selecting it (for TOC scrolling)
  const loadPeriodData = useCallback(
    async (periodKey: string) => {
      // Skip if already loaded or currently fetching
      if (
        loadedPeriods.has(periodKey) ||
        fetchingPeriodsRef.current.has(periodKey)
      ) {
        return;
      }

      fetchingPeriodsRef.current.add(periodKey);

      try {
        const lowerCapDate = moment(billingStartDate);
        const currentPeriod = getPeriodKeyByOffsetCustom(0, groupBy);

        let lowerCapPeriod: string;

        if (groupBy === 'WEEK') {
          lowerCapPeriod = getCustomWeekPeriodKey(lowerCapDate);
        } else if (groupBy === 'QUARTER') {
          const quarter = Math.floor(lowerCapDate.month() / 3) + 1;

          lowerCapPeriod = `${lowerCapDate.year()}-Q${quarter}`;
        } else {
          lowerCapPeriod = lowerCapDate.format('YYYY-MM');
        }

        const periodDate = getPeriodStartCustom(periodKey, groupBy);
        const lowerCapPeriodDate = getPeriodStartCustom(
          lowerCapPeriod,
          groupBy,
        );
        const currentPeriodDate = getPeriodStartCustom(currentPeriod, groupBy);

        // Only load if period is within valid range
        if (
          !periodDate.isSameOrAfter(lowerCapPeriodDate) ||
          !periodDate.isSameOrBefore(currentPeriodDate)
        ) {
          fetchingPeriodsRef.current.delete(periodKey);

          return;
        }

        const dateRange = getDateRangeForPeriodCustom(periodKey, groupBy);

        const requestParams: any = {
          groupBy: groupBy,
          startTime: dateRange.startDate,
          endTime: dateRange.endDate,
        };

        if (view) {
          requestParams.view = view;
        }

        const response = await SignalOccurrencesAPI.getTimeline(
          companyId,
          requestParams,
        );

        // Group timelines by period from the grouped response
        const periods = groupTimelinesByPeriod(
          response || {},
          groupBy,
          periodKey,
        );

        // Merge new data without changing selection
        if (periods.length > 0) {
          const merged = mergeTimelinePeriods(allData, periods, groupBy);

          setAllData(merged);
          setLoadedPeriods(
            new Set([...loadedPeriods, ...periods.map((p) => p.periodKey)]),
          );
        } else {
          // Even if no data, mark as loaded
          const merged = ensurePeriodExists([...allData], periodKey);

          setAllData(merged);
          setLoadedPeriods(new Set([...loadedPeriods, periodKey]));
        }
      } catch (error) {
        logger.error('Error loading period data', { periodKey, error });
      } finally {
        fetchingPeriodsRef.current.delete(periodKey);
      }
    },
    [allData, loadedPeriods, companyId, billingStartDate, groupBy, view],
  );

  return {
    data: allData,
    selectedPeriodKey,
    isInitializing,
    isLoadingSelectedPeriod,
    isLoadingDown,
    isLoadingUp,
    isLoadingTOC,
    handlePeriodSelect,
    loadEarlierPeriods,
    loadLaterPeriods,
    loadPeriodData,
  };
};
